import JobApplicationOffer from './JobApplicationOffer'
import BigNumber from 'bignumber.js'
import cloneDeep from 'lodash/cloneDeep'
import unescape from 'lodash/unescape'
import { FileCategories } from '@/constants/backend/FileCategories'
import { ModerationStages } from '@/constants/backend/ModerationStages'
import { Blockchain } from '@/constants/blockchain'
import { BACKEND_CURRENCY_USDT_ID, CURRENCY_FIELD_BACKEND_ID } from '@/constants/currency'
import { JobApplicationStatuses } from '@/constants/job/jobApplicationStatuses'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import { JobStage, STAGE_NEW, STAGE_STARTED } from '@/constants/job/jobStages'
import { JobStatus } from '@/constants/job/jobStatuses'
import File from '@/models-ts/File'
import Skill from '@/models-ts/Skill'
import { getCurrency } from '@/utils-ts/currencies'
import { stripDescriptionTags } from '@/utils-ts/strings'
import { addDays } from '@/utils/date'
import { getUrl } from '@/utils/file'
import { formatCurrency } from '@/utils/moneyFormat'
import { parseSlug } from '@/utils/parser'
import UserInfo from '../user/UserInfo'

export default class MyCustomerJobListItem {
  id: number
  sc_id: string
  status: JobStatus
  stage: JobStage
  moderationStage: ModerationStages
  slug: string
  name: string
  description: string
  budget: string
  escrow_balance: string | null
  blockchain: Blockchain
  currency: number
  estimate: number | null
  offersCount: number
  offersCountNew: number
  offers: Array<any>
  skills: Array<Skill>
  freelancer: UserInfo | null
  customer_id: number
  freelancer_id?: number
  inProgressAt: string
  completedAt: string | null
  txid_created: string | null
  txid_completed: string | null
  contract_version: number | null
  customer_wallet: string
  job_application_id?: number
  isDone: boolean
  hasReview: boolean
  created_at: string
  updated_at: string
  files: Array<File>

  constructor(data: Partial<MyCustomerJobListItem>) {
    Object.assign(this, cloneDeep(data))
  }

  static fromServer(data: LX.MyCustomerJobListItem) {
    const apps = (data.relations.Application || []).filter((item) => item.status === JobApplicationStatuses.NEW)
    return new MyCustomerJobListItem({
      id: data.id,
      sc_id: data.sc_id,
      status: data.status,
      stage: data.stage,
      created_at: data.created_at,
      updated_at: data.updated_at,
      moderationStage: data.moderation_stage,
      slug: parseSlug(data.slug),
      name: unescape(data.name),
      description: stripDescriptionTags(data.description, { stripLinks: true }),
      budget: data.budget || '0.00',
      blockchain: data.blockchain,
      currency: data.currency || BACKEND_CURRENCY_USDT_ID,
      offersCount: apps.length,
      offersCountNew: apps.filter((item) => !item.is_read).length,
      offers: (data.relations.Offer || []).map(JobApplicationOffer.fromServer),
      escrow_balance: data.escrow_balance,
      estimate: data.deadline ? data.deadline / 86400 : 0, // from seconds to day
      inProgressAt: data.in_progress_at,
      completedAt: data.completed_at,
      txid_created: data.txid_created,
      txid_completed: data.txid_completed,
      contract_version: data.contract_version,
      customer_wallet: data.customer_wallet,
      customer_id: data.customer_id,
      freelancer_id: data.freelancer_id,
      skills: (data.relations.Skill || []).map(Skill.fromServer),
      freelancer: Array.isArray(data.relations.Freelancer) ? null : UserInfo.fromServer(data.relations.Freelancer),
      job_application_id: (data.relations.Offer || []).find((offer) => offer.stage === JobOfferStages.ACCEPTED_BY_CUSTOMER)
        ?.job_application_id,
      // @ts-ignore
      hasReview: Array.isArray(data.relations.Review) ? false : !!data.relations.Review,
      isDone: Boolean(data.is_done),
      files: (data.relations?.File || []).map(File.fromServer),
    })
  }

  get wasStarted() {
    return ![STAGE_NEW, STAGE_STARTED].includes(this.stage)
  }

  get currencyModel() {
    return getCurrency({ value: this.currency, blockchain: this.blockchain, field: CURRENCY_FIELD_BACKEND_ID })
  }

  get deadline() {
    if (this.inProgressAt) {
      const inProgressAt = this.inProgressAt
      const estimate = this.estimate || 0
      return addDays(inProgressAt, estimate)
    }
  }

  get escrowBalance() {
    if (this.escrow_balance && this.currencyModel) {
      return new BigNumber(this.escrow_balance).dividedBy(this.currencyModel.baseUnits)
    }
  }

  get sortField() {
    return this.updated_at || this.created_at
  }

  get document() {
    const pdf = this.files.find((file: any) => file.category === FileCategories.CAT_GENERATED_PDF)
    return pdf && getUrl(pdf)
  }

  getBudgetFormat() {
    if (this.currencyModel) {
      if (this.escrowBalance) {
        return formatCurrency(this.escrowBalance, { currency: this.currencyModel })
      }
      return formatCurrency(this.budget, { currency: this.currencyModel })
    }
  }

  get hasAcceptedOffer() {
    return this.offers.some((offer: JobApplicationOffer) => offer?.stage === JobOfferStages.ACCEPTED_BY_CUSTOMER)
  }
}
